/**
 * Google Analytics 4 helper for the Postmoro SPA.
 *
 * WHY THIS EXISTS
 * ---------------
 * The GA4 tag in public/index.html loads gtag.js and configures the
 * property. On a normal multi-page site, GA4's Enhanced Measurement
 * observes `pushState` / `popstate` and fires a `page_view` on every
 * navigation for free.
 *
 * On a React Router 7 SPA (this app) the observer does NOT reliably
 * fire on router-initiated navigations — verified empirically in
 * preview by clicking real <Link> elements and watching network
 * traffic: only the initial load produced a page_view. To keep the
 * one-page_view-per-route guarantee the product owner asked for, we
 * fire page_views manually here, and DISABLE the auto page_view via
 * `send_page_view: false` in the gtag config (public/index.html).
 *
 * The paired hook is `useGAPageViewTracking` in
 * src/hooks/useGAPageViewTracking.js. That hook is the sole caller
 * of `trackPageView` below and it runs once per React Router
 * location change. Do not call trackPageView from anywhere else —
 * every extra call is a duplicate page_view.
 *
 * PII policy
 * ----------
 * `page_location` is always sanitised to `origin + pathname` — no
 * search string, no hash fragment, no auth codes, no reset tokens,
 * no session IDs. The sanitiser here is a second layer of defence
 * on top of the identical sanitiser in the inline gtag config.
 */

// Redact the URL of ALL potentially secret query strings + hashes.
// Postmoro URLs that legitimately carry secrets in the query string
// include /auth-callback (?code, ?state, ?session_id), /reset-password
// (?token), Meta / X / LinkedIn OAuth return trips, and Stripe /
// billing return URLs.
export function sanitizePageLocation(url) {
  if (!url || typeof url !== "string") return undefined;
  try {
    const parsed = new URL(
      url,
      typeof window !== "undefined" ? window.location.origin : "https://postmoro.com"
    );
    return parsed.origin + parsed.pathname;
  } catch (_) {
    // Fallback for malformed input — strip search + hash lexically.
    return url.split("?")[0].split("#")[0];
  }
}

/**
 * Fire exactly one GA4 page_view for the current React Router
 * location. Never call this from anywhere except the paired hook.
 *
 * @param {string} pathname  The current pathname from useLocation().
 * @param {string} [title]   Optional page title; defaults to document.title.
 */
export function trackPageView(pathname, title) {
  if (typeof window === "undefined") return; // SSR / tests
  if (typeof window.gtag !== "function") return; // gtag.js not loaded (ad-block, CSP, offline)

  const origin =
    typeof window.location !== "undefined" ? window.location.origin : "";
  const safePath =
    typeof pathname === "string" && pathname.length > 0 ? pathname : "/";
  const page_location = sanitizePageLocation(origin + safePath);
  const page_title =
    (title && String(title)) ||
    (typeof document !== "undefined" ? document.title : "");

  try {
    window.gtag("event", "page_view", {
      page_location,
      page_title,
      // page_referrer is set by GA4 from the previous page_location
      // automatically. We do not pass a custom page_referrer here —
      // if we did we would need to sanitise it too.
    });
  } catch (_) {
    // Never break the app because of analytics.
  }
}
